import { z } from 'zod';
import { Api } from '@automapa/svelte-automapa-template';
import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
import { features } from '../../shared/Features/features';
import { get } from 'svelte/store';
import { AssignmentJobApi } from './AssignmentJobApi';
import { JobServiceAttributesSchema } from '../Jobs/types';
import { api } from '../../../stores/api';
import { getFilterAssignmentId } from './getFilterAssignmentId';
import { planning } from '../../../stores/store';
import { Assignment } from './Assignment';
import {
    DetailsRoutePointSchema,
    RoutePointDeltaSchema,
    RoutePointSimpleSchema,
} from '../../utilities/RoutePointsApi';
import { PatchRequestSchema, type PatchRequestType } from '@automapa/svelte-automapa-template';
import { TargetStageSchema } from './TargetStage';
import type { QueriesType } from '../telematics/types';

// https://lm.nemo.indigo/api/assignment?filter[scenario_id]=246

export const TimeSchema = z.string().regex(/^\d{1,2}:\d{2}$/);
export type TimeType = z.infer<typeof TimeSchema>;

export const TimeSchemaNullable = TimeSchema.nullable().catch(null);
export type TimeTypeNullable = z.infer<typeof TimeSchemaNullable>;

export const RoutePointAlertSchema = z.object({
    limit: z.number().nullable(),
    value: z.number().nullable(),
    fulfilled: z.boolean().nullable(),
});
export type RoutePointAlertType = z.infer<typeof RoutePointAlertSchema>;

export const RoutePointAlertsSchema = z.object({
    parcels: RoutePointAlertSchema.optional(),
    total_weight: RoutePointAlertSchema.optional(),
    non_normative_size: RoutePointAlertSchema.optional(),
});
export type RoutePointAlertsType = z.infer<typeof RoutePointAlertsSchema>;

export const RouteAlertsSchema = z.object({
    time_window: z
        .union([
            z.array(z.any()).length(0),
            z.record(
                z.string(),
                z.object({
                    from: TimeSchemaNullable,
                    to: TimeSchemaNullable,
                    eta: z.string(),
                    fulfilled: z.boolean(),
                    delivered: z.boolean().catch(false),
                })
            ),
        ])
        .transform(val => {
            return Array.isArray(val) ? {} : val; // support empty []
        })
        .catch({}),
    parcels: z.array(RoutePointAlertSchema).catch([]),
    total_weight: z.array(RoutePointAlertSchema).catch([]),
    non_normative_size: z.array(RoutePointAlertSchema).catch([]),
    // parcels: z.union([
    //     z.record(z.string(), RoutePointAlertSchema),
    //     z.array(RoutePointAlertSchema)
    // ]).optional(),
    // total_weight: z.union([
    //     z.record(z.string(), RoutePointAlertSchema),
    //     z.array(RoutePointAlertSchema)
    // ]).optional(),
    // non_normative_size: z.union([
    //     z.record(z.string(), RoutePointAlertSchema),
    //     z.array(RoutePointAlertSchema)
    // ]).optional()
});
export type RouteAlertsType = z.infer<typeof RouteAlertsSchema>;

export const RouteInfoTollSchema = z.object({
    price: z.object({
        netto: z.number(),
        brutto: z.number(),
        tax: z.number()
    }),
    distance: z.object({
        total: z.number(),
        by_road_class: z.array(z.number())
    }).optional(),
    time: z.object({
        total: z.number(),
        by_road_class: z.array(z.number())
    }).optional()
});
export type RouteInfoTollType = z.infer<typeof RouteInfoTollSchema>;

export const RouteInfoTollsSchema = z.object({
    etoll: RouteInfoTollSchema.optional(),
    highways: RouteInfoTollSchema.optional()
});
export type RouteInfoTollsType = z.infer<typeof RouteInfoTollsSchema>;

export const ReorderedRouteSchema = z.object({
    id: z.string().catch(''),
    error: z.boolean().catch(false),
    eta_m: z.number().catch(0),
    eta_with_stops_m: z.number().catch(0),
    distance_m: z.number().catch(0),
    start_time: z.string().optional(),
    start_time_m: z.number().optional(),
    order: z.array(z.number()),
    deltas: z.array(RoutePointDeltaSchema.nullable().default(null)).optional(),
    alerts: RouteAlertsSchema.optional(),
    tolls: RouteInfoTollsSchema.optional()
});
export type ReorderedRouteType = z.infer<typeof ReorderedRouteSchema>;

export const RealisationStatusRequestSchema = z.object({
    assignment_ids: z.array(z.number()),
    route_points: z.boolean().optional(),
    service_attributes: z.boolean().optional(),
});
export type RealisationStatusRequestType = z.infer<typeof RealisationStatusRequestSchema>;

/*
{
  "id": 178101,
  "notes": 0,
  "photos": 0,
  "details": {
    "jobs": 8,
    "unset": 0,
    "parcels": 8,
    "suggestions": 0,
    "realisations": 0,
    "total_weight": 18
  },
  "completed": null,
  "deliveries": 0,
  "downloaded": null
}
 */

export const AssignmentJobServiceAttributesSimpleSchema = z.object({
    job_id: z.number(),
    attributes: JobServiceAttributesSchema.nullable(),
});
export type AssignmentJobServiceAttributesSimpleType = z.infer<
    typeof AssignmentJobServiceAttributesSimpleSchema
>;

export const RouteOptimizeSolutionSchema = z.object({
    id: z.number(),
    x: z.number(),
    y: z.number(),
    t: z.number().nullable().catch(null),
    tw_to: z.number().nullable().catch(null),
    tw_from: z.number().nullable().catch(null),
    drive_time: z.number().optional(),
    eta_pretty: TimeSchemaNullable,
    etd_pretty: TimeSchemaNullable,
    tw_from_pretty: TimeSchemaNullable,
    tw_till_pretty: TimeSchemaNullable,
    constraints_met: z.boolean().optional(),
});
export type RouteOptimizeSolutionType = z.infer<typeof RouteOptimizeSolutionSchema>;

export const RouteInfoRouteSchema = z.object({
    id: z.string().catch(''),
    inprogress: z.boolean().catch(false),
    error: z.boolean().catch(false),
    request: z.string().optional(),
    modified: z.boolean().catch(false).describe('is route modified , ex by changing start time'),
    reordered: z.boolean().catch(false).describe('is route reordered manually?'),
    modifiedBy: z
        .object({
            role: z.string(),
            id: z.number(),
        })
        .optional(),
    optimized: z.boolean().optional(),
    incremental: z.boolean().optional(),
    ordered: z.boolean().optional(),
    invalid: z.string().optional(),
    distance_m: z.number().catch(0),
    eta_m: z.number().catch(0),
    eta_with_stops_m: z.number().catch(0),
    start_time_m: z.number().optional(),
    tolls: RouteInfoTollsSchema.optional(),
    count_jobs: z.number().optional(),
    downloaded: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)
        .nullable()
        .catch(null),
    completed: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)
        .nullable()
        .catch(null),
    points: z
        .array(
            z.object({
                // delta: z
                //     .object({
                //         d: z.number().default(0),
                //         g: z
                //             .object({ d: z.number(), m: z.number(), t: z.number() })
                //             .default({ d: 0, m: 0, t: 0 })
                //             .describe('https://er.automapa.pl/issues/45353'),
                //         t: z.number().default(0),
                //     })
                //     .nullable(),
                delta: RoutePointDeltaSchema.nullable().catch(null),
                id: z.number(),
                job_delay: z.array(z.any()).nullable(),
                jobs: z.array(z.number()).nullable(),
            })
        )
        .optional(),
    alerts: RouteAlertsSchema.optional(),
});
export type RouteInfoRouteType = z.infer<typeof RouteInfoRouteSchema>;

export const EstimationRouteSchema = z.object({
    eta_m: z.number().nullable().catch(null),
    distance_m: z.number().nullable().catch(null),
    eta_with_stops_m: z.number().nullable().catch(null),
    eta_diff: z.number().nullable().catch(null),
    distance_diff: z.number().nullable().catch(null),
    route_cost: z.number().nullable().catch(null),
    count_jobs: z.number().nullable().catch(null),
    tolls: RouteInfoTollsSchema.optional(),
    estimate_key: z
        .string()
        .describe('unique route key, needed to comparing')
        .nullable()
        .catch(null),
    points: z
        .array(
            z.object({
                x: z.number(),
                y: z.number(),
                id: z.number(),
            })
        )
        .catch([]),
    alerts: RouteAlertsSchema.optional(),
});
export type EstimationRouteType = z.infer<typeof EstimationRouteSchema>;

export const RealisationStatusSchema = z.object({
    id: z.number(),
    base_route: RouteInfoRouteSchema.nullable().catch(null),
    current_route: RouteInfoRouteSchema.nullable().catch(null),
    downloaded: z.string().nullable().catch(null),
    completed: z.string().nullable().catch(null),
    invalid: z.string().nullable().catch(null),
    alerts: RouteAlertsSchema.optional(),
    details: z
        .object({
            jobs: z.number().catch(0),
            parcels: z.number().catch(0),
            realisations: z.number().catch(0),
            suggestions: z.number().catch(0),
            total_weight: z.number().catch(0),
            unset: z.number().catch(0).describe('???'),
        })
        .nullable()
        .catch(null),
    deliveries: z.number().catch(0),
    notes: z.number().catch(0),
    photos: z.number().catch(0),
    route_points: z.array(RoutePointSimpleSchema).catch([]),
    service_attributes: z.array(AssignmentJobServiceAttributesSimpleSchema).catch([]),
});
export type RealisationStatusType = z.infer<typeof RealisationStatusSchema>;

// export const RouteInfoSchema = z.any(); // FIXME: replace with proper schema RouteInfoSchemaNOTACTIVE
export const RouteInfoSchema = z
    .object({
        counters: z
            .object({
                route: z.object({ done: z.number(), total: z.number() }),
                segments: z.object({ done: z.number(), total: z.number(), cached: z.number() }),
                optimization: z.object({ done: z.number(), total: z.number() }),
            })
            .optional(),
        optimize: z
            .object({
                request: z.string().optional(),
                solution: z
                    .union([z.array(RouteOptimizeSolutionSchema), z.boolean()])
                    .optional()
                    .describe('"message": "Expected array, received boolean"'),
            })
            .optional(),
        options: z
            .union([
                z.object({
                    trace: z.boolean().optional(),
                    incremental: z.boolean().optional(),
                    start_minutes: z.number().optional(),
                    vehicle_limits: z
                        .object({
                            parcels: z.number().nullable(),
                            total_weight: z.number().nullable(),
                        })
                        .optional(),
                    ordered: z.boolean().optional()
                }),
                z.array(z.any()).length(0),
            ])
            .transform(val => {
                return Array.isArray(val) ? {} : val; // support empty []
            })
            .optional(),
        route: RouteInfoRouteSchema.optional(),
        current: RouteInfoRouteSchema.optional(),
        estimate: EstimationRouteSchema.optional(),
        estimate_suggestions: EstimationRouteSchema.optional(),
        estimate_suggestions_adhocs: EstimationRouteSchema.optional(),
        estimate_adhocs: EstimationRouteSchema.optional(),
        progress: z
            .object({
                done: z.number(),
                total: z.number(),
                percent: z.number(),
            })
            .optional(),
        segments: z
            .object({
                tasks: z.number(),
                matrix: z.any().describe('may be big - consider to strip out this field'),
                request: z.string().optional(),
            })
            .optional(),
        stage: z
            .string()
            .nullable()
            .catch(null)
            .describe('completed | routing | check_route_request | check_optimize_request etc.'),
        webuser_id: z.number().nullable().catch(null),
        precache: z.any().optional(),
        alerts: RouteAlertsSchema.optional(),
        reorder_stack: z.array(ReorderedRouteSchema).optional().catch(undefined), // NOT WORKS - https://er.automapa.pl/issues/41702
        //reorder_stack: z.any().catch([]),
        realisation_status: RealisationStatusSchema.optional(), // z.any().optional(), // RealisationStatusType
    })
    .nullable();
// .catch(null);
export type RouteInfoType = z.infer<typeof RouteInfoSchema>;
// export type RouteInfoTypeNOTACTIVE = z.infer<typeof RouteInfoSchemaNOTACTIVE>;

export const AssignmentRequestSchema = z.object({
    scenario_id: z.number().nullable(),
    vehicle_id: z.number().nullable(),
    zone_id: z.number().nullable(),
    committed: z.boolean(), // .catch(true),
    // locked: z.boolean().catch(false),
    valid_from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)
        .nullable()
        .catch(null),
    start_time_route: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)
        .catch('2024-01-01 03:30:00'),
    name: z.string().catch(''),
    color: z
        .string()
        .regex(/^#[0-9a-f]{6}$/i)
        .nullable()
        .catch(null),
    route_info: RouteInfoSchema,
    // timestamps: z.any().nullable(),
    assoc_count: z.record(z.string(), z.number()).describe('ex."assignment_job": 4').optional(),
    jobs: z.coerce.number().nullable().catch(null).describe('DEPRECATED DUE TO FEATURE 1168'),
    parcels: z.coerce.number().nullable().catch(null).describe('DEPRECATED DUE TO FEATURE 1168'),
    total_weight: z.coerce
        .number()
        .nullable()
        .catch(null)
        .describe('DEPRECATED DUE TO FEATURE 1168'),
    type: z
        .union([
            z.literal('default'),
            z.literal('unset'), // nieprzypisane zlecenia
            z.literal('adhoc'), // zlecenia dodatkowe
            z.literal('group-parent'), // assignment bedacy grupa pojazdów
            z.literal('group-child'), // assignmnet będący członkiem grupy pojazdów -- obecnie nieużywany
            z.literal('fake'), //sztuczny assignment używany przy porównywaniu planów
        ])
        .catch('default'),
});
export type AssignmentRequestType = z.infer<typeof AssignmentRequestSchema>;

export const AssignmentResponseSchema = AssignmentRequestSchema.merge(
    z.object({
        id: z.number(),
        revision: z.string().catch(''),
        updated: z.string(),
        created: z.string(),
        details: z
            .object({
                jobs: z.number().catch(0),
                parcels: z.number().catch(0),
                parcels_to_deliver: z.number().catch(0),
                parcels_to_collect: z.number().catch(0),
                realisations: z.number().catch(0),
                suggestions: z.number().catch(0),
                total_weight: z.number().catch(0),
                total_weight_to_deliver: z.number().catch(0),
                total_weight_to_collect: z.number().catch(0),
                unset: z.number().catch(0),
            })
            .optional(),
        stage_target: TargetStageSchema,
        stage_target_processed: z.boolean().nullable().catch(null), // null for adhoc and unassigned assignments
    })
);
export type AssignmentResponseType = z.infer<typeof AssignmentResponseSchema>;

export const ReorderRouteRequestParamsSchema = z.object({
    order: z.array(z.number()).catch([]).optional(),
    index: z.number().optional(),
    variant: z.number().optional(),
    start_date: z.string().optional(),
});
export type ReorderRouteRequestParamsType = z.infer<typeof ReorderRouteRequestParamsSchema>;

export const ReorderRouteRequestSchema = z.object({
    action: z.string(),
    params: ReorderRouteRequestParamsSchema,
});
export type ReorderRouteRequestType = z.infer<typeof ReorderRouteRequestSchema>;

export const ReorderRouteResponseSchema = z.object({
    route: ReorderedRouteSchema.optional(),
    variant: z.number().optional(),
    new_point_id: z.number().optional(),
    status: z.number(),
});
export type ReorderRouteResponseType = z.infer<typeof ReorderRouteResponseSchema>;

export const RealisationStatusResponseSchema = z.array(RealisationStatusSchema);
export type RealisationStatusResponseType = z.infer<typeof RealisationStatusResponseSchema>;

// {"eta_m":55,"distance_m":16904,"eta_with_stops_m":559,"points":[{"x":21.00778,"y":52.33129,"id":0},{"x":20.9769779,"y":52.3199,"id":1},{"x":20.9639926,"y":52.31946,"id":6},{"x":20.9633535,"y":52.32144,"id":5},{"x":20.9623077,"y":52.32204,"id":7},{"x":20.9594898,"y":52.32274,"id":8},{"x":20.9545513,"y":52.323,"id":3},{"x":20.9577468,"y":52.31448,"id":2},{"x":20.9582697,"y":52.31414,"id":4},{"x":21.00778,"y":52.33129,"id":9}]}

export class AssignmentApi {
    public static async getAll(api: Api, assIds: number[]): Promise<AssignmentResponseType[]> {
        let f = new ApiFilterBuilder(1000);
        return api.getData<any, AssignmentResponseType[]>(
            'post',
            '/assignment/query',
            {
                id: { $in: assIds },
            },
            z.object({ id: z.object({ $in: z.array(z.number()) }) }),
            z.array(AssignmentResponseSchema)
        );
    }

    public static async removeRouteInfo(api: Api, id: number): Promise<AssignmentResponseType> {
        return api.patch<AssignmentResponseType>(
            `/assignment/${id}`,
            'replace',
            '/route_info',
            null,
            AssignmentResponseSchema
        );
    }

    public static async setRouteInfo(
        api: Api,
        id: number,
        routeInfo: any
    ): Promise<AssignmentResponseType> {
        return api.patch<AssignmentResponseType>(
            `/assignment/${id}`,
            'replace',
            '/route_info',
            routeInfo,
            AssignmentResponseSchema
        );
    }

    public static async setStartDateRoute(
        api: Api,
        id: number,
        datetime: string
    ): Promise<AssignmentResponseType> {
        // YYYY-MM-DD HH:MM:SS  or 'NOW()'
        z.string()
            .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)
            .or(z.literal('NOW()'))
            .parse(datetime);
        return api.getData<PatchRequestType, AssignmentResponseType>(
            'patch',
            '/assignment/' + id,
            [{ op: 'replace', path: '/start_time_route', value: datetime }],
            PatchRequestSchema,
            AssignmentResponseSchema
        );
    }

    public static async getByScenarioId(
        api: Api,
        scenarioId: number
    ): Promise<AssignmentResponseType[]> {
        let f = new ApiFilterBuilder(null);
        // f.addCount('assignment_job');
        return api.getData<any, AssignmentResponseType[]>(
            'post',
            '/assignment/query',
            {
                scenario_id: scenarioId,
            },
            z.any(),
            z.array(AssignmentResponseSchema)
        );
    }

    public static async getWithCountersByScenarioId__DEPRECATED1168(
        api: Api,
        scenarioId: number,
        target_date: string,
        counters: string[] = ['jobs', 'parcels', 'total_weight']
    ) {
        return api.getData<any, AssignmentResponseType[]>(
            'post',
            '/assignments/getWithCounters?projection=' +
                counters.map(c => 'counters.' + c).join(','),
            {
                $or: [{ scenario_id: scenarioId }, { id: 0 }],
                x_target_date: target_date,
            },
            z.any(),
            z.array(AssignmentResponseSchema)
        );
    }

    public static async getWithCountersByScenarioId(
        api: Api,
        scenarioId: number,
        target_date: string
    ): Promise<AssignmentResponseType[]> {
        return api.getData<any, AssignmentResponseType[]>(
            'post',
            '/assignments/query',
            {
                scenario_id: scenarioId,
                x_target_date: target_date,
            },
            z.any(),
            z.array(AssignmentResponseSchema)
        );
    }

    public static async getOneWithCountersByAssignmentId(
        api: Api,
        assignmentId: number
    ): Promise<AssignmentResponseType> {
        return api
            .getData<any, AssignmentResponseType[]>(
                'post',
                '/assignments/query',
                {
                    id: assignmentId,
                },
                z.any(),
                z.array(AssignmentResponseSchema)
            )
            .then(response => {
                // return Promise.resolve(response[0]);
                return response[0];
            });
    }

    public static async getOne(api: Api, id: number): Promise<AssignmentResponseType> {
        return api.getData<undefined, AssignmentResponseType>(
            'get',
            '/assignment/' + id,
            undefined,
            z.undefined(),
            AssignmentResponseSchema
        );
    }

    public static async getOneWithCounters(
        api: Api,
        idOrDay: number | string,
        counters: string[] = ['jobs', 'parcels', 'total_weight']
    ): Promise<AssignmentResponseType> {
        let request: any = { id: 0 };
        if (typeof idOrDay == 'number') {
            request.id = idOrDay;
        } else if (idOrDay.match(/^\d+$/)) {
            request.id = parseInt(idOrDay);
        } else {
            request.x_target_date = idOrDay;
        }
        return api
            .getData<
                any,
                AssignmentResponseType[]
            >('post', '/assignments/query', request, z.any(), z.array(AssignmentResponseSchema))
            .then(response => {
                return Promise.resolve(response[0]);
            });
    }

    public static async setVehicleId(
        api: Api,
        id: number,
        vehicleId: number | null
    ): Promise<AssignmentResponseType> {
        return api.patch<AssignmentResponseType>(
            '/assignment/' + id,
            'replace',
            '/vehicle_id',
            vehicleId,
            AssignmentResponseSchema
        );
    }

    public static async setColor(
        api: Api,
        id: number,
        color: string
    ): Promise<AssignmentResponseType> {
        return api.patch<AssignmentResponseType>(
            '/assignment/' + id,
            'replace',
            '/color',
            color,
            AssignmentResponseSchema
        );
    }

    // public static async getOneUnassigned(api: Api, scenarioId: number) {
    //     let f = new ApiFilterBuilder(1);
    //     f.addFilterByString('type', 'unset');
    //     f.addFilterByNumber('scenario_id', scenarioId);
    //     f.addCount('assignment_job');
    //     return api.getData<undefined, AssignmentResponseType[]>(
    //         'get',
    //         '/assignment/' + f.getUri(),
    //         undefined,
    //         z.undefined(),
    //         z.array(AssignmentResponseSchema)
    //     );
    // }

    public static async getUnassignedAsList(
        api: Api,
        target_date: string,
        counters: string[] = ['jobs', 'parcels', 'total_weight']
    ) {
        let f = new ApiFilterBuilder(1);
        return api
            .getData<{ id: number; x_target_date: string }, AssignmentResponseType[]>(
                'post',
                '/assignments/query',
                {
                    id: 0,
                    x_target_date: target_date,
                },
                z.object({
                    id: z.number(),
                    x_target_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/i),
                }),
                z.array(AssignmentResponseSchema)
            )
            .then(response => {
                return Promise.resolve(response[0]);
            });
    }

    public static async setLocked(
        api: Api,
        id: number,
        locked: boolean,
        forceDownload: boolean = false
    ) {
        if (locked && (get(features).feature_1360_track_vehicles_random || forceDownload)) {
            await this.setStartDateRoute(api, id, 'NOW()');
            await this.setRouteDownloaded(api, id);
        }
        return api.patch<AssignmentResponseType>(
            '/assignment/' + id,
            'replace',
            '/committed',
            locked,
            AssignmentResponseSchema
        );
    }

    public static async confirmAllJobs(api: Api, id: number, day: string) {
        let query = {
            query: {
                //$or: [{ assignment_id: id }, { suggested_assignment_id: id }],
                suggested_assignment_id: id,
            },
            assignment_id: id,
            source: id,
        };
        // let query = {
        //     query: {
        //         $and: [
        //             { assignment_id: { $in: [0, id] } },
        //             { $or: [{ suggested_assignment_id: null }, { suggested_assignment_id: id }] },
        //         ],
        //         x_target_date: day,
        //         job_id: { $nin: [] },
        //     },
        //     assignment_id: id,
        //     source: id,
        // };
        return AssignmentJobApi.changeAssignments(api, query);
    }

    public static async reorderRoute(
        api: Api,
        id: number,
        action: string,
        params: ReorderRouteRequestParamsType
    ) {
        return api.getData<ReorderRouteRequestType, ReorderRouteResponseType>(
            'post',
            `/assignment/${id}/reorder_route`,
            { action: action, params: params },
            ReorderRouteRequestSchema,
            ReorderRouteResponseSchema
        );
    }

    public static async getRealisationStatus(
        api: Api,
        ids: number[],
        routePoints: boolean = true,
        serviceAttributes: boolean = true
    ) {
        return api.getData<RealisationStatusRequestType, RealisationStatusResponseType>(
            'post',
            `/assignment/get_realisation_status`,
            {
                assignment_ids: ids,
                route_points: routePoints,
                service_attributes: serviceAttributes,
            },
            RealisationStatusRequestSchema,
            RealisationStatusResponseSchema
        );
    }

    public static async discardRouteModifications(api: Api, id: number) {
        return api.getData<any, any>(
            'post',
            `/assignment/${id}/discard_route_modifications`,
            {},
            z.any(),
            AssignmentResponseSchema
        );
    }

    public static async setRouteDownloaded(api: Api, id: number) {
        let datetime = new Date().toISOString().replace(/T/, ' ').replace(/\..+/, '');
        z.string()
            .regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)
            .parse(datetime);

        let ass: AssignmentResponseType;
        do {
            ass = await this.getOne(api, id);
            if (ass.route_info?.route && !ass.route_info?.route?.id) {
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        } while (ass.route_info?.route && !ass.route_info?.route?.id);
        if (ass.route_info?.route) {
            ass.route_info.route = { ...ass.route_info.route, downloaded: datetime };
            return await this.setRouteInfo(api, id, ass.route_info);
        }
        return undefined;

        // return api.getData<PatchRequestType, AssignmentResponseType>(
        //     'patch',
        //     '/assignment/' + id,
        //     [{op:"replace", path:'/route_info/downloaded', value:datetime}],
        //     PatchRequestSchema,
        //     AssignmentResponseSchema
        // );
    }

    public static async setRouteNotDownloaded(api: Api, id: number) {
        let ass: AssignmentResponseType = await this.getOne(api, id);
        if (ass.route_info?.route) {
            if (ass.route_info?.route?.downloaded) {
                ass.route_info.route.downloaded = null;
            }
            return await this.setRouteInfo(api, id, ass.route_info);
        }
        return null;

        // return api.patch<AssignmentResponseType>(
        //     '/assignment/' + id,
        //     'replace',
        //     '/route_info/downloaded',
        //     null,
        //     AssignmentResponseSchema
        // );
    }

    /*
    POST https://lastmile-test.automapa.pl/api/assignment/query?projection[0]=vehicle_id&projection[1]=id
Token: 40eb651811ed1209139dbef2ff4c8a97
Content-Type: application/json

{
  "id": {
    "$in": [
      39197
    ]
  }
}
     */

    // public static async getVehiclesForGivenAssigments(api: Api, assignmentIds: number[]) {
    //     let f = new ApiFilterBuilder(1000);
    //     f.setProjectionFields(['vehicle_id', 'id']);
    //     return api.getData<any, { id: number; vehicle_id: number }[]>(
    //         'post',
    //         '/assignment/query',
    //         {
    //             id: { $in: assignmentIds },
    //         },
    //         z.any(),
    //         z.array(z.object({ id: z.number(), vehicle_id: z.number() }))
    //     );
    // }

    // public static async getVehiclesByScenarioId(api: Api, scenarioId: number) {
    //     let f = new ApiFilterBuilder(1000);
    //     f.setProjectionFields(['vehicle_id', 'id']);
    //     return api.getData<any, { id: number; vehicle_id: number }[]>(
    //         'post',
    //         '/assignment/query',
    //         {
    //             scenario_id: scenarioId,
    //         },
    //         z.any(),
    //         z.array(z.object({ id: z.number(), vehicle_id: z.number() }))
    //     );
    // }

    public static async getEstimation(
        api: Api,
        id: number,
        withSuggestionsOrAdhocs: boolean = false,
        isLocked: boolean = false,
        selectedJobIds: number[] | null = null
    ): Promise<EstimationRouteType> {
        let with_suggestions = !isLocked && withSuggestionsOrAdhocs;
        let with_adhocs = isLocked && withSuggestionsOrAdhocs;
        let selected_job_ids =
            withSuggestionsOrAdhocs && selectedJobIds !== null ? selectedJobIds : null;
        return api.getData<any, EstimationRouteType>(
            'post',
            '/assignment/' + id + '/estimate_costs',
            { with_suggestions, with_adhocs, selected_job_ids },
            z.object({ with_suggestions: z.boolean(), with_adhocs: z.boolean() }),
            EstimationRouteSchema
        );
    }

    public static async remove(api: Api, id: number) {
        return api.delete(`/assignment/${id}`);
    }

    public static async getWithQuery(
        api: Api,
        filter: ApiFilterBuilder,
        query: QueriesType
    ): Promise<AssignmentResponseType[]> {
        return api.getData<QueriesType, AssignmentResponseType[]>(
            'post',
            '/assignment/query' + filter.getUri(),
            query,
            z.any(),
            z.array(AssignmentResponseSchema)
        );
    }
}
